/** @jsxImportSource preact */
import { h, render } from 'preact';
import { RiskSubdomainChart, getChartController } from './components/RiskSubdomainChart';
import { CategoryBarChart } from './components/CategoryBarChart';
import { DocumentView } from './components/DocumentView';
import { IncidentChart, getIncidentChartController, type StackByDim } from './components/IncidentChart';
import { IncidentView, getIncidentViewController } from './components/IncidentView';
import { IncidentDetailInline } from './components/IncidentDetailInline';
import { DocumentDetailInline } from './components/DocumentDetailInline';
import { HarmSeverityScale } from './components/HarmSeverityScale';
import { CHART_CONFIGS, INCIDENT_CHART_CONFIGS } from './data/chartConfigs';
import type { StackByOption } from './components/FilterControls';
import type { ViewMode } from './components/ViewModeToggle';
import type { ActorRole } from './data/chartConfigs';

// Inject CSS variables for theming
function injectStyles() {
  if (document.getElementById('govcharts-styles')) return;

  const style = document.createElement('style');
  style.id = 'govcharts-styles';
  // Note on !important: host pages (Webflow most notoriously) ship form-element
  // styles using !important — e.g. select { min-height: 38px !important; }.
  // Inline styles set via the React/Preact style prop are normally
  // higher-specificity than CSS, but !important rules beat them. So for the
  // structural properties Webflow tends to override (min-height, padding,
  // margin, line-height, vertical-align, font-size), we set !important here.
  // Cosmetic properties (background, color, border) stay in the inline style
  // so component-level state (e.g. active vs inactive border) still wins.
  style.textContent = `
    /* Reset inherited styles inside chart containers */
    [id^="govcharts-"], [id="risk-chart"] {
      line-height: 1.4;
      font-size: 14px;
      box-sizing: border-box;
    }
    [id^="govcharts-"] *, [id="risk-chart"] * {
      line-height: inherit;
      box-sizing: border-box;
      margin: 0;
    }
    [id^="govcharts-"] button, [id="risk-chart"] button {
      font-family: inherit;
      line-height: 1.4;
    }
    [id^="govcharts-"] label, [id="risk-chart"] label {
      font-weight: normal;
      margin-bottom: 0;
    }
    [id^="govcharts-"] input[type="checkbox"], [id="risk-chart"] input[type="checkbox"] {
      width: auto;
      height: auto;
      margin: 0;
    }
    /* Defensive resets for native form elements — host pages (e.g. Webflow)
       often style these with !important rules that overshoot our inline
       sizing, knocking the filter-bar pills out of vertical alignment. */
    [id^="govcharts-"] select,
    [id^="govcharts-"] button,
    [id="risk-chart"] select,
    [id="risk-chart"] button {
      box-sizing: border-box !important;
      min-height: 0 !important;
      min-width: 0 !important;
      max-width: none !important;
      vertical-align: middle !important;
      font-family: inherit !important;
      font-weight: 400 !important;
      box-shadow: none !important;
      text-transform: none !important;
      letter-spacing: normal !important;
    }
    [id^="govcharts-"] label,
    [id="risk-chart"] label {
      display: inline-flex !important;
      vertical-align: middle !important;
      margin: 0 !important;
      padding: 0 !important;
      min-height: 0 !important;
    }
    /* Incident View list borders — drawn with outline (outer ring) and
       inset box-shadow (row dividers) rather than the border property
       because some host pages (Webflow) scale border-width via a rem rule
       and produce 0.8px-wide lines that don't render on standard-DPI
       displays. Outline is used on the outer wrapper instead of inset
       box-shadow because the inner scroll area's vertical scrollbar sits
       flush against the wrapper's right edge and covers an inset ring;
       outline paints above content (including scrollbars) and follows
       border-radius in modern browsers. */
    .gc-incident-list {
      outline: 1px solid #9ca3af !important;
      outline-offset: -1px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08) !important;
      border: 0 !important;
      border-radius: 6px !important;
    }
    /* Header row: bottom divider only — the outer outline already frames
       the top of the list. */
    .gc-incident-list-head {
      box-shadow: inset 0 -1px 0 #9ca3af !important;
      border-bottom: 0 !important;
    }
    /* Data rows: bottom divider, plus a subtle background on hover that
       keeps the divider intact (the divider is drawn via the row's own
       inset box-shadow, which a background-only hover does not displace). */
    .gc-incident-row {
      box-shadow: inset 0 -1px 0 #9ca3af !important;
      border-bottom: 0 !important;
      transition: background 0.1s ease;
    }
    .gc-incident-row:hover {
      background: var(--gc-surface-muted, #f1f5f9) !important;
    }

    :root {
      --gc-background: transparent;
      --gc-surface: inherit;
      --gc-surface-muted: #f1f5f9;
      --gc-border: #d1d5db;
      --gc-text-muted: #6b7280;
      --gc-button-bg: #ffffff;
      --gc-button-bg-active: #e5e7eb;
      --gc-button-text: #111827;
      --gc-button-text-active: #111827;
    }
  `;
  document.head.appendChild(style);
}

// Chart type definitions
type ChartType =
  | 'risk-domain-bar'
  | 'risk-subdomain-bar'
  | 'sectors-covered-bar'
  | 'lifecycle-stages-bar'
  | 'technical-scope-bar'
  | 'legislative-status-bar'
  | 'actors-bar'
  | 'lifecycle-counts-bar'
  | 'technical-scope-counts-bar'
  | 'actors-target-bar'
  | 'legislative-status-activity-bar'
  | 'all-actors-bar'
  | 'all-actors-counts-bar'
  | 'coverage-heatmap'
  | 'incident-timeline'
  | 'incident-domain-year'
  | 'incident-domain-subdomain'
  | 'incident-domain-pie'
  | 'incident-subdomain-year'
  | 'document-view'
  | 'incident-view'
  | 'natsec-incident-view'
  | 'incident-detail'
  | 'document-detail'
  | 'harm-severity-scale'
  | 'iframe';

interface InitOptions {
  type: ChartType;
  container: string | HTMLElement;
  apiBase?: string;
  filters?: Record<string, string | string[]>;
  stackBy?: StackByOption;
  viewMode?: ViewMode;
  /** Floor for the year axis on incident charts (e.g. 2015) */
  minYear?: number;
  /**
   * Incidents dataset the chart serves from (API `src` param) — e.g.
   * 'incidents-arcola' for the Arcola-mirror pilot. Omit for the default
   * incident tracker dataset. Incident chart/view types only.
   */
  dataset?: string;
  // ---- incident-detail type -------------------------------------------
  /** incident-detail only — the incident to render. */
  incidentId?: string | number;
  /** incident-detail only — selector for the page's server-rendered copy of
   * the content, hidden once the incident has loaded. */
  fallback?: string;
  // ---- document-detail type -------------------------------------------
  /** document-detail only — the governance document to render. `fallback`
   * works as for incident-detail. */
  documentId?: string | number;
  // ---- iframe type ----------------------------------------------------
  /** iframe only — the URL to embed. Must be http(s). */
  src?: string;
  /** iframe only — height as a CSS value (number → px). Default: '600px'. */
  height?: number | string;
  /** iframe only — width as a CSS value (number → px). Default: '100%'. */
  width?: number | string;
  /** iframe only — accessible title. */
  title?: string;
  /** iframe only — feature-policy `allow` attribute. */
  allow?: string;
  /** iframe only — sandbox attribute (space-separated tokens). */
  sandbox?: string;
  /**
   * iframe only — pixels to clip off the top of the iframe content by
   * wrapping it in an overflow:hidden container with a negative top
   * margin on the iframe. Useful for Google Sheets `pubhtml` embeds
   * which leave residual chrome gridlines above the data even with
   * `chrome=false`. Default 0 (no clipping).
   */
  clipTop?: number;
}

interface ChartInstance {
  destroy: () => void;
  applyFilters: (filters: ApplyFiltersOptions) => void;
}

interface ApplyFiltersOptions {
  // stackBy accepts governance options ('coverage', 'jurisdiction', ...) or
  // incident dimensions ('domain', 'subdomain', 'entity', 'intent', 'timing').
  // Pass null to clear stacking on incident charts (e.g. pies).
  stackBy?: StackByOption | StackByDim | null;
  // Governance keys
  coverage?: string | string[];
  jurisdiction?: string | string[];
  authority?: string | string[];
  legislativeStatus?: string | string[];
  activity?: string | string[];
  appliesTo?: string | string[];
  lifecycleStage?: string | string[];
  technicalScope?: string | string[];
  riskSubdomain?: string | string[];
  riskDomain?: string | string[];
  sector?: string | string[];
  actorRole?: ActorRole;
  viewMode?: ViewMode;
  // Incident keys
  year?: string | string[];
  /** Year-≥ floor; layers on top of init-time `minYear` (effective floor = max). */
  minYear?: string | number;
  domain?: string | string[];
  subdomain?: string | string[];
  /** Membership over primary + additional subdomains (Arcola-pilot datasets) */
  allSubdomains?: string | string[];
  /** Membership over harm categories with reported harm (direct/indirect ≥ 2) */
  harmCategory?: string | string[];
  entity?: string | string[];
  intent?: string | string[];
  timing?: string | string[];
  euRisk?: string | string[];
  aiGoal?: string | string[];
  /** Subject AI-system identity (Arcola mirror only): registry-normalised
   * model family / developer, or one of the three state labels. Values are
   * NOT stable keys — Arcola re-derives them as its registry grows — so do
   * not bake them into page presets or deep links. */
  aiModel?: string | string[];
  aiDeveloper?: string | string[];
}

// Must stay in sync with `StackByDim` exported by IncidentChart. Missing
// entries here cause applyFilters() to silently drop preset stackBy values
// (e.g. `stackBy: 'euRisk'` was failing the guard and leaving the pie on
// its default domain view).
const INCIDENT_STACK_DIMS: StackByDim[] = ['domain', 'subdomain', 'allSubdomains', 'entity', 'intent', 'timing', 'aiGoal', 'aiModel', 'aiDeveloper', 'euRisk', 'highestSeverity', 'natSecImpact', 'harmCategory'];
function isIncidentStackBy(v: unknown): v is StackByDim {
  return typeof v === 'string' && (INCIDENT_STACK_DIMS as string[]).includes(v);
}

// Generic iframe embed. Used for the `type: 'iframe'` option so a CMS page
// can drop in third-party content (Google Sheets pubhtml, dashboards, etc.)
// using the same script that renders the charts.
interface IframeEmbedProps {
  src: string;
  height: string;
  width: string;
  title?: string;
  allow?: string;
  sandbox?: string;
  /**
   * When > 0, the iframe is wrapped in an overflow:hidden container of the
   * requested `height` and pulled up by `clipTop` pixels (and grown by the
   * same amount) so the topmost slice is hidden. Used to mask residual
   * Google Sheets pubhtml chrome above the data.
   */
  clipTop?: number;
}
function IframeEmbed({ src, height, width, title, allow, sandbox, clipTop }: IframeEmbedProps) {
  const clip = clipTop && clipTop > 0 ? clipTop : 0;

  if (!clip) {
    return (
      <iframe
        src={src}
        width={width}
        height={height}
        title={title ?? 'Embedded content'}
        allow={allow}
        sandbox={sandbox}
        loading="lazy"
        style={{ border: 0, display: 'block', width, height }}
      />
    );
  }

  // For the clip variant, height is always pixel-based. If a non-px string
  // came in (e.g. '80vh') we can't add to it meaningfully, so the wrapper
  // inherits the value verbatim and the iframe overshoots by `clip` px via
  // a calc(). The container's overflow:hidden then hides the offset chrome.
  const containerHeight = height;
  const iframeHeight = /^\d+(\.\d+)?(px)?$/.test(height)
    ? `${parseFloat(height) + clip}px`
    : `calc(${height} + ${clip}px)`;

  return (
    <div style={{ overflow: 'hidden', width, height: containerHeight }}>
      <iframe
        src={src}
        width={width}
        height={iframeHeight}
        title={title ?? 'Embedded content'}
        allow={allow}
        sandbox={sandbox}
        loading="lazy"
        style={{
          border: 0,
          display: 'block',
          width,
          height: iframeHeight,
          marginTop: `-${clip}px`,
        }}
      />
    </div>
  );
}

// Reject anything that isn't http(s) so a misconfigured CMS field can't
// inject `javascript:` or `data:` URLs into a host page via embed.js.
function isSafeIframeUrl(u: string): boolean {
  try {
    const url = new URL(u, window.location.href);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// Coerce a height/width value: numbers become "<n>px", strings pass through.
// Empty/null falls back to the supplied default so an unfilled CMS field
// gracefully degrades.
function coerceCssSize(raw: unknown, fallback: string): string {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'number') return Number.isFinite(raw) ? `${raw}px` : fallback;
  return String(raw);
}

// Active instances
const instances: Map<string, { unmount: () => void }> = new Map();

// Default API base (can be overridden per chart)
let defaultApiBase = '';

// Auto-detect API base from script src
function detectApiBase(): string {
  if (defaultApiBase) return defaultApiBase;

  // Try to detect from script tag
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src;
    if (src && src.includes('embed.js')) {
      try {
        const url = new URL(src);
        return `${url.protocol}//${url.host}/api`;
      } catch {
        // Ignore parsing errors
      }
    }
  }

  // Fallback to current origin
  return `${window.location.protocol}//${window.location.host}/api`;
}

/**
 * Configure global settings
 */
function configure(options: { apiBase?: string }): void {
  if (options.apiBase) {
    defaultApiBase = options.apiBase;
  }
}

/**
 * Initialise a chart
 */
function init(options: InitOptions): ChartInstance {
  const {
    type,
    container,
    apiBase = detectApiBase(),
    filters,
    stackBy,
    viewMode,
  } = options;

  // Allow minYear to be set per-chart from a CMS "Min Year" field bound
  // either as the top-level option or via the `filters` object
  // (e.g. { minYear: "2015" }). Lifts the value to the top-level minYear
  // prop so it acts as the hard floor the chart already understands.
  //
  // Empty / null / NaN / 0 are all treated as "no floor" so an empty CMS
  // field gracefully degrades to no minYear, rather than throwing or
  // pinning the chart at year 0.
  const coerceMinYear = (raw: unknown): number | undefined => {
    if (raw == null || raw === '') return undefined;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  let effectiveMinYear = coerceMinYear(options.minYear);
  if (effectiveMinYear === undefined && filters && 'minYear' in filters) {
    effectiveMinYear = coerceMinYear(filters.minYear);
  }
  // Strip minYear from filters so it doesn't double-fire as a categorical
  // filter param ("minYear=2015" is meaningless as a filter key for the
  // chart's initial categorical state).
  const childFilters = filters && 'minYear' in filters
    ? Object.fromEntries(Object.entries(filters).filter(([k]) => k !== 'minYear'))
    : filters;
  // Gov Map and incident-chart consumers (RiskSubdomainChart, DocumentView,
  // CategoryBarChart) still expect single-value filters. Coerce arrays
  // down to their first element for those paths. IncidentView (the only
  // consumer that needs multi-value, e.g. for natSecImminence presets
  // selecting Near-term + Immediate) consumes the raw array form.
  const childFiltersSingle: Record<string, string> | undefined = childFilters
    ? Object.fromEntries(
        Object.entries(childFilters).map(([k, v]) => [k, Array.isArray(v) ? (v[0] ?? '') : v])
      )
    : undefined;

  // Inject styles on first init
  injectStyles();

  // Resolve container element
  const el =
    typeof container === 'string'
      ? document.getElementById(container)
      : container;

  if (!el) {
    throw new Error(`GovCharts: Container not found: ${container}`);
  }

  // Generate ID if needed
  const id = el.id || `govcharts-${Date.now()}`;
  if (!el.id) el.id = id;

  // Destroy existing instance if any
  if (instances.has(id)) {
    instances.get(id)?.unmount();
    instances.delete(id);
  }

  // Render appropriate chart type
  if (type === 'risk-subdomain-bar') {
    render(
      <RiskSubdomainChart
        apiBase={apiBase}
        containerId={id}
        initialFilters={childFiltersSingle}
        initialStackBy={stackBy}
        initialViewMode={viewMode}
      />,
      el
    );
  } else if (type === 'document-view') {
    render(
      <DocumentView
        apiBase={apiBase}
        containerId={id}
        initialFilters={childFiltersSingle}
      />,
      el
    );
  } else if (type === 'incident-view') {
    render(
      <IncidentView
        apiBase={apiBase}
        containerId={id}
        initialFilters={childFilters}
        minYear={effectiveMinYear}
        dataset={options.dataset}
      />,
      el
    );
  } else if (type === 'natsec-incident-view') {
    render(
      <IncidentView
        apiBase={apiBase}
        containerId={id}
        initialFilters={childFilters}
        minYear={effectiveMinYear}
        mode="natsec"
        dataset={options.dataset}
      />,
      el
    );
  } else if (type === 'harm-severity-scale') {
    render(
      <HarmSeverityScale
        apiBase={apiBase}
        containerId={id}
        dataset={options.dataset}
      />,
      el
    );
  } else if (type === 'incident-detail') {
    render(
      <IncidentDetailInline
        apiBase={apiBase}
        incidentId={String(options.incidentId ?? '').trim()}
        dataset={options.dataset}
        fallback={options.fallback}
      />,
      el
    );
  } else if (type === 'document-detail') {
    render(
      <DocumentDetailInline
        apiBase={apiBase}
        documentId={String(options.documentId ?? '').trim()}
        fallback={options.fallback}
      />,
      el
    );
  } else if (INCIDENT_CHART_CONFIGS[type]) {
    render(
      <IncidentChart
        config={INCIDENT_CHART_CONFIGS[type]}
        apiBase={apiBase}
        containerId={id}
        minYear={effectiveMinYear}
        dataset={options.dataset}
      />,
      el
    );
  } else if (type === 'iframe') {
    if (!options.src || !isSafeIframeUrl(options.src)) {
      throw new Error(
        `GovCharts: iframe type requires a valid http(s) src URL (got: ${String(options.src)})`
      );
    }
    render(
      <IframeEmbed
        src={options.src}
        height={coerceCssSize(options.height, '600px')}
        width={coerceCssSize(options.width, '100%')}
        title={options.title}
        allow={options.allow}
        sandbox={options.sandbox}
        clipTop={typeof options.clipTop === 'number' && Number.isFinite(options.clipTop) ? options.clipTop : undefined}
      />,
      el
    );
  } else if (CHART_CONFIGS[type]) {
    render(
      <CategoryBarChart
        config={CHART_CONFIGS[type]}
        apiBase={apiBase}
        containerId={id}
        initialFilters={childFiltersSingle}
        initialStackBy={stackBy}
        initialViewMode={viewMode}
      />,
      el
    );
  } else {
    throw new Error(`GovCharts: Unknown or not yet implemented chart type: ${type}`);
  }

  // Store instance with unmount function
  const instance = {
    unmount: () => {
      render(null, el);
    },
  };
  instances.set(id, instance);

  // Return public API for this instance
  return {
    destroy: () => destroy(id),
    applyFilters: (filters: ApplyFiltersOptions) => applyFilters(id, filters),
  };
}

/**
 * Apply filters to a chart from external code (e.g. hyperlinks on the page)
 */
function applyFilters(containerId: string, filters: ApplyFiltersOptions): void {
  // Try the incident-chart registry first.
  const incident = getIncidentChartController(containerId);
  if (incident) {
    incident.resetFilters();
    if (filters.stackBy !== undefined) {
      if (filters.stackBy === null || isIncidentStackBy(filters.stackBy)) {
        incident.setStackBy(filters.stackBy);
      }
    }
    if (filters.year !== undefined) incident.setYearFilter(filters.year);
    if (filters.minYear !== undefined) incident.setMinYearFilter(String(filters.minYear));
    if (filters.domain !== undefined) incident.setDomainFilter(filters.domain);
    if (filters.subdomain !== undefined) incident.setSubdomainFilter(filters.subdomain);
    if (filters.allSubdomains !== undefined) incident.setAllSubdomainsFilter(filters.allSubdomains);
    if (filters.harmCategory !== undefined) incident.setHarmCategoryFilter(filters.harmCategory);
    if (filters.entity !== undefined) incident.setEntityFilter(filters.entity);
    if (filters.intent !== undefined) incident.setIntentFilter(filters.intent);
    if (filters.timing !== undefined) incident.setTimingFilter(filters.timing);
    if (filters.euRisk !== undefined) incident.setEuRiskFilter(filters.euRisk);
    if (filters.aiGoal !== undefined) incident.setAiGoalFilter(filters.aiGoal);
    if (filters.aiModel !== undefined) incident.setAiModelFilter(filters.aiModel);
    if (filters.aiDeveloper !== undefined) incident.setAiDeveloperFilter(filters.aiDeveloper);
    // viewMode controls whether stacked bars show raw incident counts
    // ('counts', default) or 100%-normalised proportions ('proportion').
    // Pie charts ignore the toggle.
    if (filters.viewMode === 'counts' || filters.viewMode === 'proportion') {
      incident.setViewMode(filters.viewMode);
    }
    // Severity thresholds — any `minSeverity*` key in the filters object
    // (e.g. minSeverityHighest, minSeverityPhysical) is collected into the
    // chart's severity filter map. Resets to empty when nothing matches.
    const severityFilters: Record<string, number> = {};
    for (const [k, v] of Object.entries(filters as Record<string, unknown>)) {
      if (!k.startsWith('minSeverity')) continue;
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      // A bare `minSeverity` means the any-category threshold: the live
      // incident-timeline preset ("…severe or catastrophic harm…") uses it,
      // and the API only knows `minSeverityHighest`, so it filtered nothing.
      if (Number.isFinite(n) && n > 0) severityFilters[k === 'minSeverity' ? 'minSeverityHighest' : k] = n;
    }
    if (Object.keys(severityFilters).length > 0) {
      incident.setSeverityFilters(severityFilters);
    }
    return;
  }

  // IncidentView / NatSec IncidentView — same applyFilters surface as
  // the charts but the view consumes the preset wholesale via its own
  // controller (because it has more state to coordinate: sort + the
  // NatSec-only categorical filters).
  const view = getIncidentViewController(containerId);
  if (view) {
    view.applyPreset(filters as Record<string, unknown>);
    return;
  }

  const controller = getChartController(containerId);
  if (!controller) {
    console.warn(`GovCharts.applyFilters: No chart found with id "${containerId}"`);
    return;
  }

  // Reset all filters to defaults first, then apply the specified ones
  controller.resetFilters();

  if (filters.stackBy !== undefined && filters.stackBy !== null && !isIncidentStackBy(filters.stackBy)) {
    controller.setStackBy(filters.stackBy as StackByOption);
  }
  if (filters.coverage !== undefined) controller.setCoverageFilter(filters.coverage);
  if (filters.jurisdiction !== undefined) controller.setJurisdictionFilter(filters.jurisdiction);
  if (filters.authority !== undefined) controller.setAuthorityFilter(filters.authority);
  if (filters.legislativeStatus !== undefined) controller.setLegislativeStatusFilter(filters.legislativeStatus);
  if (filters.activity !== undefined) controller.setActivityFilter(filters.activity);
  if (filters.appliesTo !== undefined) controller.setAppliesToFilter(filters.appliesTo);
  if (filters.lifecycleStage !== undefined) controller.setLifecycleStageFilter(filters.lifecycleStage);
  if (filters.technicalScope !== undefined) controller.setTechnicalScopeFilter(filters.technicalScope);
  if (filters.riskSubdomain !== undefined) controller.setRiskSubdomainFilter(filters.riskSubdomain);
  if (filters.riskDomain !== undefined) controller.setRiskDomainFilter(filters.riskDomain);
  if (filters.sector !== undefined) controller.setSectorFilter(filters.sector);
  if (filters.actorRole !== undefined) controller.setActorRole(filters.actorRole);
  if (filters.viewMode !== undefined) controller.setViewMode(filters.viewMode);
}

/**
 * Destroy a chart instance
 */
function destroy(containerId: string): void {
  const instance = instances.get(containerId);
  if (instance) {
    instance.unmount();
    instances.delete(containerId);
  }
}

/**
 * Destroy all chart instances
 */
function destroyAll(): void {
  instances.forEach((instance) => instance.unmount());
  instances.clear();
}

// Public API
// ── Live incident count ────────────────────────────────────────────────
// Fills every [data-gc-incident-count] element with the dataset's record
// count, formatted with thousands separators. Counts come from
// /api/sync-status — a cheap read of the per-dataset :meta keys the nightly
// sync maintains, so the number tracks the database without any page edits.
//
// Host-page usage. Anywhere you control the HTML, use the data attribute:
//   <span data-gc-incident-count>1,600</span>                default dataset
//   <span data-gc-incident-count="incidents-arcola">1,600</span>
// Mid-paragraph in Webflow, use a CLASS instead — inline text spans there
// can't carry custom attributes, but the Designer can wrap selected text in
// a span and give it a class:
//   <span class="gc-incident-count">1,600</span>             default dataset
//   <span class="gc-incident-count gc-incident-count--incidents-arcola">
// The element's existing text is the fallback: it is only replaced after a
// successful fetch, so a network failure leaves the static copy in place.
function countDatasetFor(el: HTMLElement): string {
  const attr = el.getAttribute('data-gc-incident-count');
  if (attr) return attr;
  // Substring match rather than classList tokens: typing both class names
  // into Webflow's selector field in one go slugifies the space, merging
  // them into a single token ("gc-incident-count-gc-incident-count--x").
  // Matching the modifier anywhere in className tolerates that.
  const m = /gc-incident-count--([A-Za-z0-9_-]+)/.exec(el.className);
  return m ? m[1] : 'incidents';
}

async function updateIncidentCounts(): Promise<void> {
  const els = document.querySelectorAll<HTMLElement>(
    '[data-gc-incident-count], [class*="gc-incident-count"]'
  );
  if (els.length === 0) return;
  try {
    const res = await fetch(`${detectApiBase()}/sync-status`);
    if (!res.ok) return;
    const json = (await res.json()) as {
      datasets?: Array<{ name: string; recordCount: number | null }>;
    };
    const counts = new Map((json.datasets ?? []).map((d) => [d.name, d.recordCount]));
    els.forEach((el) => {
      const count = counts.get(countDatasetFor(el));
      if (typeof count === 'number' && count > 0) {
        el.textContent = count.toLocaleString('en-US');
      }
    });
  } catch {
    // Keep the static fallback text.
  }
}

// Run automatically once the DOM is ready, so a page that only wants the
// count needs no GovCharts.init() call at all.
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { void updateIncidentCounts(); });
  } else {
    void updateIncidentCounts();
  }
}

const GovCharts = {
  configure,
  init,
  applyFilters,
  destroy,
  destroyAll,
  updateIncidentCounts,
  version: '2.0.0',
};

// Expose globally
declare global {
  interface Window {
    GovCharts: typeof GovCharts;
  }
}

window.GovCharts = GovCharts;

export default GovCharts;
